/**
 * The entity catalog's choices, apart from how they're shown: which geography to start from, the "order" (entities
 * picked, each with its toppings and what to do if the space has one like it), searching, and the request that
 * previews or applies it.
 */
import type { ApiError } from "../api/client.ts";
import type {
  CatalogOut,
  CategoryOut,
  FacetOut,
  GeographyOut,
  LinkHint,
  ObjEntity,
  OnCollision,
  TemplateApply,
  TemplateOut,
  TemplatePlan,
} from "../api/types.gen.ts";
import { fold } from "./text.ts";

/** One entity of the order. */
export interface Pick {
  key: string;
  /** Its toppings (facet keys). */
  facets: string[];
  /** When the space has an entity like it. */
  onCollision?: OnCollision;
  /** A name of its own, kept apart from the space's like it (else the catalog's, made distinct). */
  name?: string | undefined;
  /** Its optional links taken or left out, by the field's English key (absent: each one's default). */
  links?: Record<string, boolean>;
}

/** Flags whose code isn't the geography's key: International (none), the EU's own, and the UK's (GB). */
const FLAGS: Record<string, string> = { "": "🌐", eu: "🇪🇺", uk: "🇬🇧" };
/** "A" as a regional indicator symbol: two of them, a region's code, make its flag. */
const REGIONAL_A = 0x1_f1_e6;

/** A geography's flag: its code in regional indicator symbols (🇧🇷 for `br`), or one of the special ones. */
export function flagOf(geo: string): string {
  const special = FLAGS[geo];
  if (special !== undefined) {
    return special;
  }
  return String.fromCodePoint(...[...geo.toUpperCase()].map((c) => REGIONAL_A + (c.codePointAt(0) ?? 65) - 65));
}

/** A geography's name with its flag before it. */
export const flagged = (geo: string, name: string): string => `${flagOf(geo)} ${name}`;

/** Spaces between search words. */
const SPACES = /\s+/u;

/** EU members without a geography of their own: the EU's. */
const EU = new Set(
  "AT BE BG HR CY CZ DK EE FI GR HU LV LT LU MT NL PL RO SK SI SE".split(" ").map((c) => c.toLowerCase()),
);
/** Country codes whose geography is named otherwise. */
const ALIASES: Record<string, string> = { gb: "uk" };

/** The geography of a country (ISO 3166-1 alpha-2), if the catalog has one for it. */
function geographyOfCountry(country: string, geos: readonly GeographyOut[]): string | null {
  const cc = country.toLowerCase();
  const key = ALIASES[cc] ?? cc;
  if (geos.some((g) => g.key === key)) {
    return key;
  }
  return EU.has(cc) && geos.some((g) => g.key === "eu") ? "eu" : null;
}

/** The geography whose zones include `zone` (a space's time zone). */
const geographyOfZone = (zone: string, geos: readonly GeographyOut[]): string | null =>
  geos.find((g) => g.zones.includes(zone))?.key ?? null;

/** The geography a language tag suggests: its region (`en-GB`, `pt-BR`), or the country of its language's own. */
function geographyOfLocale(tag: string, geos: readonly GeographyOut[]): string | null {
  let region: string | undefined;
  try {
    region = new Intl.Locale(tag).maximize().region;
  } catch {
    return null;
  }
  // English maximizes to the US; only a region said outright counts.
  if (!(region && (tag.includes("-") || !tag.startsWith("en")))) {
    return null;
  }
  return geographyOfCountry(region, geos);
}

/** Where to start: the space's zone, else the language's region, else international (""). */
export const defaultGeography = (zone: string, tag: string, geos: readonly GeographyOut[]): string =>
  geographyOfZone(zone, geos) ?? geographyOfLocale(tag, geos) ?? "";

/** The toppings pre-selected on `e` when picked on its own: its geography's. */
const defaultFacets = (e: TemplateOut): string[] => e.facets.filter((f) => f.default).map((f) => f.key);

/** The order with `e` in or out (in with `facets`, else its defaults). */
export function toggle(order: readonly Pick[], e: TemplateOut, facets?: string[]): Pick[] {
  return order.some((p) => p.key === e.key)
    ? order.filter((p) => p.key !== e.key)
    : [...order, { key: e.key, facets: facets ?? defaultFacets(e) }];
}

/** The order with all of a category's entities in it (those already in keep their choices). */
export function addCategory(order: readonly Pick[], c: CategoryOut): Pick[] {
  const added = c.entities
    .filter((p) => !order.some((o) => o.key === p.key))
    .map((p) => ({ key: p.key, facets: [...p.facets] }));
  return [...order, ...added];
}

/** The order without a category's entities. */
export const removeCategory = (order: readonly Pick[], c: CategoryOut): Pick[] =>
  order.filter((o) => !c.entities.some((p) => p.key === o.key));

/** Whether every entity of the category is in the order. */
export const hasCategory = (order: readonly Pick[], c: CategoryOut): boolean =>
  c.entities.every((p) => order.some((o) => o.key === p.key));

/** The order with one pick changed. */
export const update = (order: readonly Pick[], key: string, change: Partial<Pick>): Pick[] =>
  order.map((p) => (p.key === key ? { ...p, ...change } : p));

/** A topping on or off. */
export function toggleFacet(order: readonly Pick[], key: string, facet: string): Pick[] {
  const p = order.find((x) => x.key === key);
  if (!p) {
    return [...order];
  }
  const facets = p.facets.includes(facet) ? p.facets.filter((f) => f !== facet) : [...p.facets, facet];
  return update(order, key, { facets });
}

/** Every entity of the catalog: the geography's, then the other countries' or regions'. */
export const allOf = (c: CatalogOut): TemplateOut[] => [...c.entities, ...c.others];

/** An entity's toppings: its geography's, then the other countries' or regions' for it. */
export const facetsOf = (e: TemplateOut): FacetOut[] => [...e.facets, ...(e.other_facets ?? [])];

/**
 * The order after the catalog changed (another geography): entities it no longer has drop out, toppings it no longer
 * offers too, and the new geography's own toppings come pre-selected. Another country's or region's entity or topping
 * stays: it can be added from anywhere.
 */
export function reconcile(order: readonly Pick[], before: CatalogOut | undefined, after: CatalogOut): Pick[] {
  const out: Pick[] = [];
  for (const p of order) {
    const e = allOf(after).find((x) => x.key === p.key);
    if (!e) {
      continue;
    }
    const was = before ? allOf(before).find((x) => x.key === p.key) : undefined;
    const had = new Set(was?.facets.map((f) => f.key));
    const offered = new Set(facetsOf(e).map((f) => f.key));
    const facets = p.facets.filter((f) => offered.has(f));
    for (const f of e.facets) {
      if (f.default && !had.has(f.key) && !facets.includes(f.key)) {
        facets.push(f.key);
      }
    }
    out.push({ ...p, facets });
  }
  return out;
}

/** Entities matching a search (name, description, aliases, field labels), accents and case aside. */
/** The categories matching every word of `q`: in their name, their description, or their entities' names. */
export function searchCategories(
  categories: readonly CategoryOut[],
  entities: readonly TemplateOut[],
  q: string,
): CategoryOut[] {
  const words = fold(q).split(SPACES).filter(Boolean);
  if (words.length === 0) {
    return [...categories];
  }
  const names = new Map(entities.map((e) => [e.key, [e.name, ...e.aliases]]));
  return categories.filter((c) => {
    const text = fold([c.name, c.description, ...c.entities.flatMap((x) => names.get(x.key) ?? [])].join(" "));
    return words.every((w) => text.includes(w));
  });
}

export function search(entities: readonly TemplateOut[], q: string): TemplateOut[] {
  const words = fold(q).split(SPACES).filter(Boolean);
  if (words.length === 0) {
    return [...entities];
  }
  return entities.filter((e) => {
    const text = fold(
      [
        e.name,
        e.description,
        ...e.aliases,
        ...e.fields.map((f) => f.label ?? f.key),
        ...e.facets.map((f) => f.name),
      ].join(" "),
    );
    return words.every((w) => text.includes(w));
  });
}

/** What an entity of the order gets: its fields and its toppings'. */
export function fieldsOf(e: TemplateOut, facets: readonly string[]) {
  return [
    ...e.fields,
    ...facetsOf(e)
      .filter((f) => facets.includes(f.key))
      .flatMap((f) => f.fields),
  ];
}

/** Other countries' or regions' toppings for `e`, by where they're from (in the catalog's order). */
export function otherFacetsByGeography(e: TemplateOut): { geography: string; facets: FacetOut[] }[] {
  const out: { geography: string; facets: FacetOut[] }[] = [];
  for (const f of e.other_facets ?? []) {
    const g = f.geography ?? "";
    const group = out.find((x) => x.geography === g);
    if (group) {
      group.facets.push(f);
    } else {
      out.push({ geography: g, facets: [f] });
    }
  }
  return out;
}

/** An optional link of a pick taken (`on`) or left out. */
export function setLink(order: readonly Pick[], key: string, field: string, on: boolean): Pick[] {
  const p = order.find((x) => x.key === key);
  return p ? update(order, key, { links: { ...p.links, [field]: on } }) : [...order];
}

/** The request previewing (`dry`) or applying the order; `english`, with the catalog's English field keys. */
export const request = (
  order: readonly Pick[],
  {
    language,
    geography,
    dry,
    english = false,
  }: { language: string; geography: string; dry: boolean; english?: boolean },
): TemplateApply => ({
  entities: order.map((p) => ({
    key: p.key,
    facets: p.facets,
    ...(p.onCollision && { on_collision: p.onCollision }),
    ...(p.onCollision === "separate" && p.name && { name: p.name }),
    ...(p.links && Object.keys(p.links).length > 0 && { links: p.links }),
  })),
  language,
  ...(geography && { geography }),
  ...(english && { keys: "english" as const }),
  dry_run: dry,
});

/** A link the plan makes: from an entity, through its field (`key` as written), to an entity added or the space's. */
export interface PlanLink {
  from: string;
  label: string;
  key: string;
  to: string;
}

/**
 * The links the plan makes for sure between entities (to ones added with them, or already in the space), in order; the
 * optional ones are the suggestions, listed (and toggled) on their own.
 */
export function planLinks(plan: TemplatePlan, existing: readonly ObjEntity[]): PlanLink[] {
  const out: PlanLink[] = [];
  for (const e of plan.entities) {
    for (const f of e.fields) {
      const link = f.links?.[0];
      const to = link ? plan.entities.find((x) => x.key === link)?.name : existing.find((x) => x.id === f.target)?.name;
      const suggested = plan.suggestions.some((x) => x.entity === e.key && x.key === f.key);
      if (f.type === "ref" && to && !suggested) {
        out.push({ from: e.name, label: f.label ?? f.key, key: f.key, to });
      }
    }
  }
  return out;
}

/** The hints, one per entity to add: the entities it would link up, by name. */
export function hintsByTarget(plan: TemplatePlan): { add: string; name: string; from: string[] }[] {
  const out: { add: string; name: string; from: string[] }[] = [];
  const nameOf = (h: LinkHint) => plan.entities.find((e) => e.key === h.entity)?.name ?? h.entity;
  for (const h of plan.hints) {
    const group = out.find((x) => x.add === h.add);
    if (!group) {
      out.push({ add: h.add, name: h.name, from: [nameOf(h)] });
    } else if (!group.from.includes(nameOf(h))) {
      group.from.push(nameOf(h));
    }
  }
  return out;
}

/** Picks still waiting for a choice between merging and keeping apart. */
export const undecided = (plan: TemplatePlan | undefined): number =>
  plan?.collisions.filter((c) => !c.resolution).length ?? 0;

/** How many links the plan makes between entities (to ones added with them, or already in the space). */
export const linkCount = (plan: TemplatePlan): number =>
  plan.entities.flatMap((e) => e.fields).filter((f) => f.type === "ref" && (f.target || (f.links ?? []).length > 0))
    .length;

/** Whether the order can go on to the review: something picked, every collision decided, nothing refused. */
export const canReview = (
  order: readonly Pick[],
  plan: TemplatePlan | undefined,
  error: ApiError | undefined,
): boolean => order.length > 0 && plan !== undefined && !error && undecided(plan) === 0;
